import { createRouter, createWebHashHistory } from "vue-router";
import DashboardView from "@/views/DashboardView.vue";
import LoginView from "@/views/LoginView.vue";
import InsightView from "@/views/InsightView.vue";
import ContactView from "@/views/ContactView.vue";
import InterView from "@/views/InterView.vue";
import PaymentView from "@/views/PaymentView.vue";
import AuthService from "@/services/AuthService";
import APIService from "@/services/APIService";
import eventBus from "@/plugins/eventBus";
import store from "@/store";

const routes = [
  {
    path: "/",
    redirect: "/about",
    meta: { public: true },
  },
  {
    path: "/auth-success",
    name: "auth-success",
    meta: { public: true },
    beforeEnter: async (to, from, next) => {
      try {
        const token = to.query.token;
        if (token) {
          sessionStorage.setItem("auth_token", token);
          await store.dispatch("tenancy/fetchUser");
          const result = await store.dispatch("tenancy/initTenant");
          if (!result?.requiresSelection) {
            await store.dispatch("tenancy/fetchUser");
            await ensureDynamicInsightsInstalled();
          }
          eventBus.emit("user-signed-in");
          if (result.requiresSelection) {
            next("/tenants");
          } else {
            next("/dashboard");
          }
        } else {
          next("/login?error=no_token");
        }
      } catch (error) {
        console.error("Auth success error:", error);
        next("/login?error=auth_failed");
      }
    },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: DashboardView,
  },
  {
    path: "/interview/:tenantId/:id",
    name: "interview",
    meta: { public: true },
    component: InterView,
  },
  {
    path: "/login",
    name: "login",
    meta: { public: true },
    component: LoginView,
  },
  {
    path: "/about",
    name: "about",
    meta: { public: true },
    component: InsightView,
  },
  {
    path: "/tenants",
    name: "tenants",
    component: () => import("@/views/TenantSelectorView.vue"),
  },
  {
    path: "/tenants/:tenantId/settings",
    name: "tenant-settings",
    component: () => import("@/views/TenantSettingsView.vue"),
  },
  {
    path: "/invite/:token",
    name: "accept-invite",
    meta: { public: true },
    component: () => import("@/views/AcceptInviteView.vue"),
  },
  {
    path: "/logout",
    name: "logout",
    beforeEnter: async (to, from, next) => {
      try {
        await AuthService.signOut();
        next("/about");
      } catch (error) {
        console.error("Error signing out:", error);
        next("/about");
      }
    },
  },
  {
    path: "/contact",
    name: "contact",
    meta: { public: true },
    component: ContactView,
  },
  {
    path: "/payment",
    name: "payment",
    component: PaymentView,
  },
  {
    path: "/subscription-success",
    name: "subscription-success",
    component: () => import("@/views/SubscriptionSuccessView.vue"),
  },
  {
    path: "/subscription-cancel",
    name: "subscription-cancel",
    component: () => import("@/views/SubscriptionCancelView.vue"),
  },
  {
    path: "/project/new",
    name: "new-project",
    component: () =>
      import(/* webpackChunkName: "project" */ "../views/ProjectView.vue"),
  },
  {
    path: "/project/:id",
    name: "edit-project",
    component: () =>
      import(/* webpackChunkName: "project" */ "../views/ProjectView.vue"),
  },
  {
    path: "/project/:id/results",
    name: "project-results",
    component: () =>
      import(
        /* webpackChunkName: "project" */ "../views/ProjectResultsView.vue"
      ),
  },
  {
    path: "/privacy",
    name: "privacy",
    meta: { public: true },
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/PrivacyPolicyView.vue"),
  },
  {
    path: "/terms",
    name: "terms",
    meta: { public: true },
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/TermsOfTradeView.vue"),
  },
];

async function ensureDynamicInsightsInstalled() {
  try {
    const tenant = store.getters["tenancy/currentTenant"];
    if (!tenant?.tenant_id) return;
    const tenantRes = await APIService.getTenant(tenant.tenant_id);
    const installedApps = tenantRes.data.installed_apps || [];
    if (!installedApps.includes("dynamic_insights")) {
      await APIService.installApp("dynamic_insights");
    }
  } catch {
    // Tenant fetch failed — not a blocker
  }
}

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    next();
    return;
  }

  try {
    if (!AuthService.isAuthenticated()) {
      next("/login");
      return;
    }

    const hasTenant =
      store.getters["tenancy/currentTenant"] ||
      sessionStorage.getItem("current_tenant");

    if (to.path !== "/tenants" && !hasTenant) {
      next("/tenants");
      return;
    }

    if (from.path === "/tenants" && hasTenant) {
      store.dispatch("tenancy/fetchUser");
      ensureDynamicInsightsInstalled();
    }

    next();
  } catch (error) {
    console.error("Error checking authentication:", error);
    next("/login");
  }
});

export default router;
